// ===== Encryption Key Management Utility =====
// Generates RSA-OAEP key pair, stores both keys in IndexedDB ("user details" DB),
// and loads them back for client-side decryption.

const DB_NAME = "user details";
const DB_VERSION = 1;
const STORE_NAME = "keys";

// Call backend directly (not via Next.js proxy) so request goes to backend URL
const getPublicKeyApiUrl = (): string => {
  const base =
    typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_BASE_URL
      ? process.env.NEXT_PUBLIC_API_BASE_URL.replace(/\/$/, "")
      : process.env.NEXT_PUBLIC_API_BASE_URL;
  return `${base}/api/meeting_notes/public_key`;
};

// ---------- IndexedDB Helpers ----------

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ---------- Key Generation ----------

async function generateKeyPair(): Promise<CryptoKeyPair> {
  return await window.crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt"]
  );
}

// ---------- Store Keys ----------

async function storePrivateKey(privateKey: CryptoKey): Promise<void> {
  // Export key BEFORE opening transaction to avoid auto-commit issue
  const exportedKey = await crypto.subtle.exportKey("pkcs8", privateKey);

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.put(exportedKey, "privateKey");

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function storePublicKey(publicKey: CryptoKey): Promise<void> {
  // Export key BEFORE opening transaction to avoid auto-commit issue
  const exportedKey = await crypto.subtle.exportKey("spki", publicKey);

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.put(exportedKey, "publicKey");

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------- Store / Load flag ----------

async function setFlag(key: string, value: boolean): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.put(value, key);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getFlag(key: string): Promise<boolean> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const request = store.get(key);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(!!request.result);
    request.onerror = () => reject(request.error);
  });
}

const LAST_PUBLIC_KEY_POST_DATE_KEY = "lastPublicKeyPostDate";

function getLocalDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

async function getStoredString(key: string): Promise<string | null> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const request = store.get(key);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const v = request.result;
      if (v === undefined || v === null) return resolve(null);
      resolve(typeof v === "string" ? v : null);
    };
    request.onerror = () => reject(request.error);
  });
}

async function setStoredString(key: string, value: string): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.put(value, key);

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---------- Load Keys ----------

async function loadPrivateKey(): Promise<CryptoKey | null> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const request = store.get("privateKey");

  return new Promise((resolve, reject) => {
    request.onsuccess = async () => {
      if (!request.result) return resolve(null);

      const privateKey = await crypto.subtle.importKey(
        "pkcs8",
        request.result,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        true,
        ["decrypt"]
      );

      resolve(privateKey);
    };
    request.onerror = () => reject(request.error);
  });
}

async function loadPublicKey(): Promise<CryptoKey | null> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const request = store.get("publicKey");

  return new Promise((resolve, reject) => {
    request.onsuccess = async () => {
      if (!request.result) return resolve(null);

      const publicKey = await crypto.subtle.importKey(
        "spki",
        request.result,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        true,
        ["encrypt"]
      );

      resolve(publicKey);
    };
    request.onerror = () => reject(request.error);
  });
}

// ---------- Conversion Helpers ----------

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function hexToArrayBuffer(hex: string): ArrayBuffer {
  // Trim whitespace and validate (matches sample tool's hexToBytes)
  hex = hex.trim().replace(/\s+/g, "");
  if (hex.length % 2 !== 0) {
    throw new Error("Invalid hex string (odd length): " + hex.length);
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes.buffer;
}

async function exportKeyToBase64(
  key: CryptoKey,
  format: "spki" | "pkcs8"
): Promise<string> {
  const exported = await crypto.subtle.exportKey(format, key);
  return arrayBufferToBase64(exported);
}

/**
 * Exports a public key in PEM format (base64 with headers, 64-char line wrapping).
 * Most server-side crypto libraries (Python cryptography, OpenSSL, etc.) expect PEM.
 */
async function exportKeyToPEM(key: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey("spki", key);
  const b64 = arrayBufferToBase64(exported);
  // Wrap at 64 characters per line (PEM standard)
  const lines = b64.match(/.{1,64}/g) || [];
  return `-----BEGIN PUBLIC KEY-----\n${lines.join("\n")}\n-----END PUBLIC KEY-----`;
}

// ---------- Decryption Helpers ----------

async function decryptAESKey(
  encryptedKeyHex: string,
  privateKey: CryptoKey
): Promise<CryptoKey> {
  const encryptedKeyBuffer = hexToArrayBuffer(encryptedKeyHex);
  const aesKeyBuffer = await crypto.subtle.decrypt(
    { name: "RSA-OAEP" },
    privateKey,
    encryptedKeyBuffer
  );
  return await crypto.subtle.importKey(
    "raw",
    aesKeyBuffer,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );
}

async function decryptData(
  encryptedHex: string,
  aesKey: CryptoKey,
  iv: ArrayBuffer
): Promise<string> {
  const encryptedBuffer = hexToArrayBuffer(encryptedHex);
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    aesKey,
    encryptedBuffer
  );
  return new TextDecoder().decode(decryptedBuffer);
}

/**
 * Decrypts blob data (ArrayBuffer) using AES-GCM
 * @param encryptedHex - Hex-encoded encrypted data
 * @param aesKey - AES-GCM key
 * @param iv - Initialization vector
 * @returns Decrypted ArrayBuffer
 */
async function decryptBlobData(
  encryptedHex: string,
  aesKey: CryptoKey,
  iv: ArrayBuffer
): Promise<ArrayBuffer> {
  const encryptedBuffer = hexToArrayBuffer(encryptedHex);
  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    aesKey,
    encryptedBuffer
  );
  return decryptedBuffer;
}

async function decryptField(
  value: any,
  aesKey: CryptoKey,
  iv: ArrayBuffer
): Promise<any> {
  if (typeof value === "string" && value.length > 0) {
    try {
      const decrypted = await decryptData(value, aesKey, iv);
      // The decrypted string may be a JSON array/object (e.g. agenda items, action_items).
      // Parse it so the UI receives proper arrays/objects instead of raw JSON text.
      try {
        return JSON.parse(decrypted);
      } catch (_jsonErr) {
        return decrypted;
      }
      } catch (_e) {
        // console.warn("decryptField: failed to decrypt a string value, returning as-is");
        return value;
    }
  }
  if (Array.isArray(value)) {
    // Decrypt each element, then flatten: if an element decrypted into an array
    // (e.g. one encrypted string → ["item1","item2",...]), spread it into the parent.
    const results = await Promise.all(
      value.map((item) => decryptField(item, aesKey, iv))
    );
    // Flatten one level: [["a","b"], "c"] → ["a","b","c"]
    const flattened: any[] = [];
    for (const r of results) {
      if (Array.isArray(r)) {
        flattened.push(...r);
      } else {
        flattened.push(r);
      }
    }
    return flattened;
  }
  return value;
}

// ---------- Decrypt Download Blob ----------

/**
 * Decrypts an encrypted file blob from the download API
 * @param encryptedResponse - Response object with encryption metadata (is_encrypted, file_data, public_keys, iv)
 * @param userEmail - User's email to find the correct encrypted AES key
 * @returns Decrypted ArrayBuffer of the file data
 */
export async function decryptDownloadBlob(
  encryptedResponse: any,
  userEmail: string
): Promise<ArrayBuffer | null> {
  try {
    // If not encrypted, return null (caller should handle raw blob)
    if (!encryptedResponse.is_encrypted) {
      // console.log("Download response is not encrypted — returning null");
      return null;
    }

    // Load private key from IndexedDB
    const privateKey = await loadPrivateKey();
    if (!privateKey) {
      // console.error("Private key not found in IndexedDB — cannot decrypt");
      return null;
    }

    const publicKeys = encryptedResponse.public_keys || {};
    const allEmails = Object.keys(publicKeys);
    // console.log("Download: public_keys entries:", allEmails);
    // console.log("Download: User email from session:", userEmail);

    // Skip "server@tredence.com" — that is the server's own key, not the user's
    const SERVER_KEY = "server@tredence.com";
    const userEmails = allEmails.filter((e) => e !== SERVER_KEY);
    // console.log("Download: User keys (after skipping server):", userEmails);

    if (userEmails.length === 0) {
      // console.error(
      //   "Download: No user keys found in public_keys (only server key present) — cannot decrypt"
      // );
      return null;
    }

    // Step 1: Find the user's encrypted AES key
    let encryptedAESKeyHex = "";
    let matchedEmail = "";

    if (userEmail) {
      // Exact match
      const exactMatch = userEmails.find((e) => e === userEmail);
      if (exactMatch) {
        encryptedAESKeyHex = publicKeys[exactMatch];
        matchedEmail = exactMatch;
      }

      // Case-insensitive match
      if (!encryptedAESKeyHex) {
        const lowerEmail = userEmail.toLowerCase();
        const caseMatch = userEmails.find(
          (e) => e.toLowerCase() === lowerEmail
        );
        if (caseMatch) {
          encryptedAESKeyHex = publicKeys[caseMatch];
          matchedEmail = caseMatch;
        }
      }
    }

    // Fallback: use the first non-server key
    if (!encryptedAESKeyHex) {
      matchedEmail = userEmails[0];
      encryptedAESKeyHex = publicKeys[matchedEmail];
      // console.log(
      //   `Download: No exact email match — using first available user key: "${matchedEmail}"`
      // );
    }

    // console.log(`Download: Using encrypted AES key from "${matchedEmail}"`);

    // Step 2: Decrypt AES key with RSA-OAEP
    // console.log("Download: Decrypting AES key with RSA-OAEP private key...");
    const aesKey = await decryptAESKey(encryptedAESKeyHex, privateKey);
    // console.log("Download: AES key decrypted successfully");

    // Step 3: Decrypt file data with AES-GCM
    const iv = base64ToArrayBuffer(encryptedResponse.iv);
    // console.log(
    //   `Download: IV decoded from base64: ${iv.byteLength} bytes (expect 12 for AES-GCM)`
    // );

    // Get encrypted file data (could be in file_data, blob_data, or data field)
    const encryptedFileDataHex =
      encryptedResponse.file_data ||
      encryptedResponse.blob_data ||
      encryptedResponse.data;

    if (!encryptedFileDataHex) {
      // console.error("Download: No encrypted file data found in response");
      return null;
    }

    // console.log("Download: Decrypting file blob data...");
    const decryptedBuffer = await decryptBlobData(
      encryptedFileDataHex,
      aesKey,
      iv
    );
    // console.log("Download: ✅ File blob decrypted successfully");
    return decryptedBuffer;
  } catch (error) {
    // console.error("Download: Error decrypting file blob:", error);
    return null;
  }
}

// ---------- Decrypt Meeting Response ----------

export async function decryptMeetingResponse(
  response: any,
  userEmail: string
): Promise<any> {
  try {
    // If not encrypted, return as-is
    if (!response.is_encrypted) {
      // console.log("Response is not encrypted — returning as-is");
      return response;
    }

    // Load private key from IndexedDB
    const privateKey = await loadPrivateKey();
    if (!privateKey) {
      // console.error("Private key not found in IndexedDB — cannot decrypt");
      return response;
    }

    const publicKeys = response.public_keys || {};
    const allEmails = Object.keys(publicKeys);
    // console.log("public_keys entries:", allEmails);
    // console.log("User email from session:", userEmail);

    // Skip "server@tredence.com" — that is the server's own key, not the user's
    const SERVER_KEY = "server@tredence.com";
    const userEmails = allEmails.filter((e) => e !== SERVER_KEY);
    // console.log("User keys (after skipping server):", userEmails);

    if (userEmails.length === 0) {
      // console.error(
      //   "No user keys found in public_keys (only server key present) — cannot decrypt"
      // );
      return response;
    }

    // Step 1: Find the user's encrypted AES key
    // Priority: exact email match → case-insensitive match → first available user key
    let encryptedAESKeyHex = "";
    let matchedEmail = "";

    if (userEmail) {
      // Exact match (only among non-server keys)
      const exactMatch = userEmails.find((e) => e === userEmail);
      if (exactMatch) {
        encryptedAESKeyHex = publicKeys[exactMatch];
        matchedEmail = exactMatch;
      }

      // Case-insensitive match
      if (!encryptedAESKeyHex) {
        const lowerEmail = userEmail.toLowerCase();
        const caseMatch = userEmails.find(
          (e) => e.toLowerCase() === lowerEmail
        );
        if (caseMatch) {
          encryptedAESKeyHex = publicKeys[caseMatch];
          matchedEmail = caseMatch;
        }
      }
    }

    // Fallback: use the first non-server key
    if (!encryptedAESKeyHex) {
      matchedEmail = userEmails[0];
      encryptedAESKeyHex = publicKeys[matchedEmail];
      // console.log(
      //   `No exact email match — using first available user key: "${matchedEmail}"`
      // );
    }

    // console.log(`Using encrypted AES key from "${matchedEmail}"`);

    // Diagnostic: log sizes to verify data integrity
    // console.log(
    //   `Encrypted AES key: ${encryptedAESKeyHex.length} hex chars = ${encryptedAESKeyHex.length / 2} bytes (expect 256 for RSA-2048)`
    // );
    // console.log(`IV (raw from API): "${response.iv}"`);

    // Step 2: Decrypt AES key with RSA-OAEP using private key from IndexedDB
    // console.log("Decrypting AES key with RSA-OAEP private key...");
    const aesKey = await decryptAESKey(encryptedAESKeyHex, privateKey);
    // console.log("AES key decrypted and imported as AES-GCM key successfully");

    // Step 3: Decrypt ciphertext with AES-GCM
    // Decode IV (base64 → ArrayBuffer)
    const iv = base64ToArrayBuffer(response.iv);
    // console.log(
    //   `IV decoded from base64: ${iv.byteLength} bytes (expect 12 for AES-GCM)`
    // );

    // Clone the response to avoid mutating original
    const decrypted = JSON.parse(JSON.stringify(response));

    // Decrypt subject field if it exists and looks encrypted (long hex string)
    if (decrypted.subject && typeof decrypted.subject === "string") {
      // Check if subject looks like an encrypted hex string (64+ hex characters)
      if (/^[0-9a-f]{64,}$/i.test(decrypted.subject.trim())) {
        decrypted.subject = await decryptField(
          decrypted.subject,
          aesKey,
          iv
        );
        // console.log("✅ Subject decrypted");
      }
    }

    // Decrypt summary fields
    if (decrypted.summary) {
      for (const key of Object.keys(decrypted.summary)) {
        decrypted.summary[key] = await decryptField(
          decrypted.summary[key],
          aesKey,
          iv
        );
      }
      // console.log("✅ Summary decrypted");
    }

    // Decrypt transcript
    if (decrypted.transcript && typeof decrypted.transcript === "string") {
      decrypted.transcript = await decryptField(
        decrypted.transcript,
        aesKey,
        iv
      );
      // console.log("✅ Transcript decrypted");
    }

    // Decrypt mom_json fields
    if (decrypted.mom_json) {
      for (const key of Object.keys(decrypted.mom_json)) {
        const val = decrypted.mom_json[key];
        if (
          typeof val === "object" &&
          val !== null &&
          !Array.isArray(val) &&
          val.payload
        ) {
          // Handle objects with payload (e.g., per_speaker)
          decrypted.mom_json[key] = {
            ...val,
            payload: await decryptField(val.payload, aesKey, iv),
          };
        } else {
          decrypted.mom_json[key] = await decryptField(val, aesKey, iv);
        }
      }
      // console.log("✅ MOM JSON decrypted");
    }

    decrypted.is_encrypted = false;
    // console.log("✅ Meeting response fully decrypted");
    return decrypted;
  } catch (error) {
    // console.error("Error decrypting meeting response:", error);
    return response;
  }
}

// ---------- Decrypt Summary Item Field ----------

/**
 * Decrypts a single field (e.g., subject) from a summary item.
 * The summary item may have encryption metadata at the item level or inherit it from the parent response.
 * @param encryptedValue - The encrypted hex string value to decrypt
 * @param encryptionMetadata - Object with is_encrypted, iv, and public_keys (can be at item level or response level)
 * @param userEmail - User's email to find the correct encrypted AES key
 * @returns Decrypted string value, or original value if decryption fails or not encrypted
 */
export async function decryptSummaryItemField(
  encryptedValue: string,
  encryptionMetadata: { is_encrypted?: boolean; iv?: string; public_keys?: Record<string, string> },
  userEmail: string
): Promise<string> {
  try {
    // If not encrypted, return as-is
    if (!encryptionMetadata.is_encrypted || !encryptionMetadata.iv || !encryptionMetadata.public_keys) {
      return encryptedValue;
    }

    // Load private key from IndexedDB
    const privateKey = await loadPrivateKey();
    if (!privateKey) {
      return encryptedValue;
    }

    const publicKeys = encryptionMetadata.public_keys || {};
    const allEmails = Object.keys(publicKeys);

    // Skip "server@tredence.com" — that is the server's own key, not the user's
    const SERVER_KEY = "server@tredence.com";
    const userEmails = allEmails.filter((e) => e !== SERVER_KEY);

    if (userEmails.length === 0) {
      return encryptedValue;
    }

    // Step 1: Find the user's encrypted AES key
    // Priority: exact email match → case-insensitive match → first available user key
    let encryptedAESKeyHex = "";
    let matchedEmail = "";

    if (userEmail) {
      // Exact match (only among non-server keys)
      const exactMatch = userEmails.find((e) => e === userEmail);
      if (exactMatch) {
        encryptedAESKeyHex = publicKeys[exactMatch];
        matchedEmail = exactMatch;
      }

      // Case-insensitive match
      if (!encryptedAESKeyHex) {
        const lowerEmail = userEmail.toLowerCase();
        const caseMatch = userEmails.find(
          (e) => e.toLowerCase() === lowerEmail
        );
        if (caseMatch) {
          encryptedAESKeyHex = publicKeys[caseMatch];
          matchedEmail = caseMatch;
        }
      }
    }

    // Fallback: use the first non-server key
    if (!encryptedAESKeyHex) {
      matchedEmail = userEmails[0];
      encryptedAESKeyHex = publicKeys[matchedEmail];
    }

    // Step 2: Decrypt AES key with RSA-OAEP using private key from IndexedDB
    const aesKey = await decryptAESKey(encryptedAESKeyHex, privateKey);

    // Step 3: Decrypt ciphertext with AES-GCM
    // Decode IV (base64 → ArrayBuffer)
    const iv = base64ToArrayBuffer(encryptionMetadata.iv);

    // Step 4: Decrypt the field
    const decrypted = await decryptField(encryptedValue, aesKey, iv);
    
    // decryptField may return a parsed JSON value, but for subject we expect a string
    return typeof decrypted === "string" ? decrypted : encryptedValue;
  } catch (error) {
    // console.error("Error decrypting summary item field:", error);
    return encryptedValue;
  }
}

// ---------- Decrypt Markdown Body ----------

/**
 * Decrypts a markdown body that contains encrypted hex strings embedded in the text.
 * The body typically has sections like "## Agenda\n- <hex_string>" where hex_string needs to be decrypted.
 * @param body - Markdown string with embedded encrypted hex strings
 * @param encryptionMetadata - Object with is_encrypted, iv, and public_keys
 * @param userEmail - User's email to find the correct encrypted AES key
 * @returns Decrypted markdown body with hex strings replaced by decrypted text
 */
export async function decryptMarkdownBody(
  body: string,
  encryptionMetadata: { iv: string; public_keys: Record<string, string> },
  userEmail: string
): Promise<string> {
  try {
    // Load private key from IndexedDB
    const privateKey = await loadPrivateKey();
    if (!privateKey) {
      // console.error("Private key not found in IndexedDB — cannot decrypt");
      return body;
    }

    const publicKeys = encryptionMetadata.public_keys || {};
    const allEmails = Object.keys(publicKeys);
    // console.log("Decrypt markdown body: public_keys entries:", allEmails);
    // console.log("Decrypt markdown body: User email from session:", userEmail);

    // Skip "server@tredence.com" — that is the server's own key, not the user's
    const SERVER_KEY = "server@tredence.com";
    const userEmails = allEmails.filter((e) => e !== SERVER_KEY);
    // console.log("Decrypt markdown body: User keys (after skipping server):", userEmails);

    if (userEmails.length === 0) {
      // console.error(
      //   "Decrypt markdown body: No user keys found in public_keys (only server key present) — cannot decrypt"
      // );
      return body;
    }

    // Step 1: Find the user's encrypted AES key
    let encryptedAESKeyHex = "";
    let matchedEmail = "";

    if (userEmail) {
      // Exact match (only among non-server keys)
      const exactMatch = userEmails.find((e) => e === userEmail);
      if (exactMatch) {
        encryptedAESKeyHex = publicKeys[exactMatch];
        matchedEmail = exactMatch;
      }

      // Case-insensitive match
      if (!encryptedAESKeyHex) {
        const lowerEmail = userEmail.toLowerCase();
        const caseMatch = userEmails.find(
          (e) => e.toLowerCase() === lowerEmail
        );
        if (caseMatch) {
          encryptedAESKeyHex = publicKeys[caseMatch];
          matchedEmail = caseMatch;
        }
      }
    }

    // Fallback: use the first non-server key
    if (!encryptedAESKeyHex) {
      matchedEmail = userEmails[0];
      encryptedAESKeyHex = publicKeys[matchedEmail];
      // console.log(
      //   `Decrypt markdown body: No exact email match — using first available user key: "${matchedEmail}"`
      // );
    }

    // console.log(`Decrypt markdown body: Using encrypted AES key from "${matchedEmail}"`);

    // Step 2: Decrypt AES key with RSA-OAEP
    // console.log("Decrypt markdown body: Decrypting AES key with RSA-OAEP private key...");
    const aesKey = await decryptAESKey(encryptedAESKeyHex, privateKey);
    // console.log("Decrypt markdown body: AES key decrypted successfully");

    // Step 3: Decode IV
    const iv = base64ToArrayBuffer(encryptionMetadata.iv);
    // console.log(
    //   `Decrypt markdown body: IV decoded from base64: ${iv.byteLength} bytes (expect 12 for AES-GCM)`
    // );

    // Step 4: Find and decrypt all hex strings in the markdown body
    // Pattern to match encrypted hex strings (long hex strings after "- " in markdown lists)
    // Hex strings are typically 64+ characters long
    const hexPattern = /- ([0-9a-f]{64,})/gi;
    let decryptedBody = body;
    const matches: RegExpExecArray[] = [];
    let match: RegExpExecArray | null;
    
    // Collect all matches
    while ((match = hexPattern.exec(body)) !== null) {
      matches.push(match);
    }

    // console.log(`Decrypt markdown body: Found ${matches.length} encrypted hex strings to decrypt`);

    for (const matchItem of matches) {
      const hexString = matchItem[1];
      try {
        const decryptedText = await decryptData(hexString, aesKey, iv);
        // Replace the hex string with decrypted text
        decryptedBody = decryptedBody.replace(matchItem[0], `- ${decryptedText}`);
        // console.log(`Decrypt markdown body: Successfully decrypted hex string (length: ${hexString.length})`);
      } catch (decryptErr) {
        // console.warn("Decrypt markdown body: Failed to decrypt hex string, keeping original:", decryptErr);
        // Keep the original hex string if decryption fails
      }
    }

    // console.log("Decrypt markdown body: ✅ Markdown body decrypted successfully");
    return decryptedBody;
  } catch (error) {
    // console.error("Decrypt markdown body: Error decrypting markdown body:", error);
    return body; // Return original body if decryption fails
  }
}

// ---------- Reset Keys (manual recovery) ----------

/**
 * Clears all keys and flags from IndexedDB.
 * Call from browser console for manual recovery:
 *   const { resetEncryptionKeys } = await import("/src/utils/cryptoKeys");
 *   await resetEncryptionKeys();
 * Then refresh the page to regenerate and re-post keys.
 */
export async function resetEncryptionKeys(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  store.clear();

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      // console.log("resetEncryptionKeys: All keys and flags cleared from IndexedDB");
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Clears the "posted today" marker so the next authenticated session will POST the public key again
 * (e.g. after logout/login the same day). Call from sign-out handlers.
 */
export async function clearLastPublicKeyPostDate(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete(LAST_PUBLIC_KEY_POST_DATE_KEY);
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IndexedDB may be unavailable (SSR, private mode); ignore
  }
}

// ---------- Sync key pair with backend (POST + GET fallback) ----------

type PostPublicKeyToApiResult =
  | "success"
  | "keys_already_on_server"
  | "failed";

async function postPublicKeyToAPI(
  publicKeyBase64: string,
  privateKeyBase64: string,
  accessToken: string
): Promise<PostPublicKeyToApiResult> {
  try {
    const response = await fetch(getPublicKeyApiUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        public_key: publicKeyBase64,
        private_key: privateKeyBase64,
      }),
    });

    if (response.ok) {
      return "success";
    }
    // Server indicates this user's key material already exists — next step is GET.
    if (response.status === 400 || response.status === 409) {
      return "keys_already_on_server";
    }
    return "failed";
  } catch {
    return "failed";
  }
}

function parseKeyPairFromJsonResponse(
  data: unknown
): { public_key: string; private_key: string } | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const root = data as Record<string, unknown>;
  let pub: unknown = root.public_key;
  let priv: unknown = root.private_key;
  if (root.data && typeof root.data === "object") {
    const inner = root.data as Record<string, unknown>;
    if (typeof inner.public_key === "string") {
      pub = inner.public_key;
    }
    if (typeof inner.private_key === "string") {
      priv = inner.private_key;
    }
  }
  if (typeof pub === "string" && typeof priv === "string" && pub && priv) {
    return { public_key: pub, private_key: priv };
  }
  return null;
}

/**
 * Fetches the user's RSA key pair (raw base64 SPKI + PKCS#8) that the server already stored.
 * Called when POST /public_key returns 4xx "already exists".
 */
async function fetchEncryptionKeyPairFromServer(
  accessToken: string
): Promise<{ public_key: string; private_key: string } | null> {
  try {
    const response = await fetch(getPublicKeyApiUrl(), {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!response.ok) {
      return null;
    }
    const data: unknown = await response.json();
    return parseKeyPairFromJsonResponse(data);
  } catch {
    return null;
  }
}

/**
 * Imports server-provided key material and overwrites IndexedDB "keys" (privateKey / publicKey).
 */
async function storeServerKeyPairInIndexedDB(
  publicKeyBase64: string,
  privateKeyBase64: string
): Promise<boolean> {
  try {
    const privateKey = await crypto.subtle.importKey(
      "pkcs8",
      base64ToArrayBuffer(privateKeyBase64),
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["decrypt"]
    );
    const publicKey = await crypto.subtle.importKey(
      "spki",
      base64ToArrayBuffer(publicKeyBase64),
      { name: "RSA-OAEP", hash: "SHA-256" },
      true,
      ["encrypt"]
    );
    await storePrivateKey(privateKey);
    await storePublicKey(publicKey);
    return true;
  } catch {
    return false;
  }
}

// ---------- Main Orchestrator ----------

export type InitializeEncryptionKeysOptions = {
  /**
   * When true, runs the public key POST/GET sync on every call (e.g. each /home load or refresh).
   * When false/omitted, sync runs at most once per local calendar day.
   */
  alwaysSyncPublicKey?: boolean;
};

export async function initializeEncryptionKeys(
  accessToken?: string,
  options?: InitializeEncryptionKeysOptions
): Promise<void> {
  try {
    // console.log("initializeEncryptionKeys: started");
    // console.log("initializeEncryptionKeys: accessToken present:", !!accessToken);

    // Check if a PEM-formatted key was posted previously.
    // If so, regenerate and re-post as raw base64 (without PEM headers).
    const pemKeyPosted = await getFlag("publicKeyPostedPEM");
    const rawBase64Posted = await getFlag("publicKeyPostedRawBase64");

    if (pemKeyPosted && !rawBase64Posted) {
      // console.warn(
      //   "PEM key was posted but raw base64 was never posted — key format mismatch. " +
      //     "Clearing all keys and regenerating..."
      // );
      await resetEncryptionKeys();
      // After reset, fall through to the "no keys" path below
    }

    // Load RSA pair from IndexedDB, or (first visit / no keys) generate in the browser via Web Crypto.
    const existingPrivateKey = await loadPrivateKey();
    const existingPublicKey = await loadPublicKey();
    const hadStoredKeyPair = !!(existingPrivateKey && existingPublicKey);

    let publicKey: CryptoKey;
    let privateKey: CryptoKey;

    if (hadStoredKeyPair) {
      publicKey = existingPublicKey;
      privateKey = existingPrivateKey;
    } else {
      // First-time (or after reset): no key material — create RSA-OAEP pair, persist to IndexedDB.
      const keyPair = await generateKeyPair();
      await storePrivateKey(keyPair.privateKey);
      await storePublicKey(keyPair.publicKey);
      publicKey = keyPair.publicKey;
      privateKey = keyPair.privateKey;
    }

    // Public key API: first-time or returning users. Sync with server when we have a token and either:
    // (1) the user had no local pair and we just generated it — always register via POST, or
    // (2) / home requested a refresh each visit (alwaysSyncPublicKey), or
    // (3) we have a stored pair and have not yet synced today.
    if (accessToken) {
      const today = getLocalDateString();
      const lastPostDate = await getStoredString(LAST_PUBLIC_KEY_POST_DATE_KEY);
      const shouldSyncWithServer =
        !hadStoredKeyPair ||
        options?.alwaysSyncPublicKey === true ||
        lastPostDate !== today;

      if (shouldSyncWithServer) {
        const publicKeyBase64 = await exportKeyToBase64(publicKey, "spki");
        const privateKeyBase64 = await exportKeyToBase64(privateKey, "pkcs8");
        // console.log("Posting public key to API (raw base64, no PEM headers)...");
        // console.log("Public key base64 preview:", publicKeyBase64.substring(0, 60) + "...");
        const postResult = await postPublicKeyToAPI(
          publicKeyBase64,
          privateKeyBase64,
          accessToken
        );
        if (postResult === "success") {
          await setStoredString(LAST_PUBLIC_KEY_POST_DATE_KEY, today);
          await setFlag("publicKeyPostedRawBase64", true);
          await setFlag("publicKeyPosted", true);
        } else if (postResult === "keys_already_on_server") {
          const pair = await fetchEncryptionKeyPairFromServer(accessToken);
          if (pair) {
            const applied = await storeServerKeyPairInIndexedDB(
              pair.public_key,
              pair.private_key
            );
            if (applied) {
              await setStoredString(LAST_PUBLIC_KEY_POST_DATE_KEY, today);
              await setFlag("publicKeyPostedRawBase64", true);
              await setFlag("publicKeyPosted", true);
            }
          }
        }
      } else {
        // console.log("Public key already posted today — skipping");
      }
    } else {
      // console.log("No accessToken available — skipping API post");
    }
  } catch (error) {
    // console.error("Error initializing encryption keys:", error);
  }
}
